import { useEffect, useRef, useState } from "react";
import Icon from "../../../Elements/Icon/Icon.tsx";
import AnimationContainer from "components/Reusable/AnimationContainer/AnimationContainer.js";
import useMediaQueries from "hooks/useMediaQueries.js";
import { throttle } from "lodash";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { useUploaderInput } from "../Helpers/useUploaderInput.js";
import { useUploader } from "../../../../contexts/uploaderContext/uploaderContext.js";
import { ContactsCircle } from "./ContactCircle/ContactsCircle.js";
import { useContent } from "contexts/contentContext/contentContext.js";
import { useDragUploader } from "../Helpers/useDragUploader.js";
import { useUser } from "contexts/userContext/userContext.js";

const MILISECONDS_THROTTLE_VALUE = 100;
const TOOLTIP_MARGIN = window.innerHeight > 850 ? 120 : -30;

export const DropZone = () => {
  const { getUploader, setRecipientEmail } = useUploader();
  const { getTextByKey, getHtmlByKey } = useContent();
  const { isLoggedIn, isSubscribed } = useUser();
  const [drag, setDrag] = useState(false);
  const [hover, setHover] = useState(false);
  const [contactName, setContactName] = useState(null);
  const [containerStyle, setContainerStyle] = useState({
    zIndex: 999,
  });
  const [showEmail, setShowEmail] = useState(false);
  const filesRef = useRef();
  const folderRef = useRef();
  const outerCircleRef = useRef();
  const tooltipContainerRef = useRef();
  const { mobileCondition, desktopCondition } = useMediaQueries();

  const marketingCondition = !isLoggedIn() || !isSubscribed();

  const additionalDragFunc = (event) => {
    // if dragged on avatar
    if (event.target.classList.contains("contact")) {
      const email = event.target.getAttribute("data-email");
      setDrag(email);
      const name = event.target.getAttribute("data-name");
      if (name) setContactName(name);
    } else if (event.target.classList.contains("contact-name")) {
      const email = event.target.parentElement.getAttribute("data-email");
      setDrag(email);
      const name =
        event.target.parentElement.parentElement.getAttribute("data-name");
      if (name) setContactName(name);
    } else {
      setDrag(true);
    }
  };

  const additionalDropFunc = (event) => {
    const target = event.target.classList.contains("contact")
      ? event.target
      : event.target.parentElement;
    const email = target.getAttribute("data-email");
    if (email) {
      setRecipientEmail(email);
    }
  };

  const { handleAddFiles, handleAddFolders } = useUploaderInput({
    filesRef,
    folderRef,
  });

  const { handleDrop, handleDrag, dragged } = useDragUploader({
    additionalDragFunc: additionalDragFunc,
    additionalDropFunc: additionalDropFunc,
  });

  const handleScroll = (e) => {
    if (desktopCondition) {
      if (window.scrollY > 0) {
        setContainerStyle((prev) => ({ ...prev, zIndex: 0 }));
      } else {
        setContainerStyle((prev) => ({ ...prev, zIndex: 999 }));
      }
    }
  };

  const throttleScroll = throttle(() => {
    handleScroll();
  }, MILISECONDS_THROTTLE_VALUE);

  useEffect(() => {
    if (!getUploader()) return;
    const containerName = document.querySelector(".home-main-container");
    containerName.addEventListener("drageenter", handleDrag);
    containerName.addEventListener("dragover", handleDrag);
    containerName.addEventListener("dragleave", handleDrag);
    containerName.addEventListener("drop", handleDrop);

    return () => {
      containerName.removeEventListener("drageenter", handleDrag);
      containerName.removeEventListener("dragover", handleDrag);
      containerName.removeEventListener("dragleave", handleDrag);
      containerName.removeEventListener("drop", handleDrop);
    };
  }, [getUploader()]);

  useEffect(() => {
    if (drag && typeof drag === "string") {
      setShowEmail(true);
    } else {
      setShowEmail(false);
    }
  }, [drag, dragged]);

  useEffect(() => {
    if (tooltipContainerRef.current && outerCircleRef.current) {
      // get height of tooltip
      const tooltipHeight = tooltipContainerRef.current.offsetHeight;
      setContainerStyle((prev) => ({
        ...prev,
        top: -tooltipHeight - TOOLTIP_MARGIN,
      }));
    }
  }, [tooltipContainerRef, outerCircleRef, isLoggedIn()]);

  useEffect(() => {
    window.addEventListener("scroll", throttleScroll);
    return () => {
      window.removeEventListener("scroll", throttleScroll);
    };
  }, []);

  useEffect(() => {
    if (!desktopCondition) {
      setContainerStyle((prev) => ({ ...prev, zIndex: 0 }));
    }
  }, [desktopCondition]);

  return (
    <div className="home-drop-zone">
      {/* {createPortal()} */}
      <AnimationContainer
        animationOn={!(drag && mobileCondition)}
        animationClassName="fade-in"
      >
        {!mobileCondition && (
          <div
            className="dropzone-tooltip-container"
            ref={tooltipContainerRef}
            style={containerStyle}
          >
            <div className="dropzone-tooltip-text">
              {marketingCondition && (
                <div className="increaser-limit">
                  <Icon name="jm-marketing-icon" />
                  <LinkToNewTab page="plans">
                    {getTextByKey("increaser-limit")}
                  </LinkToNewTab>
                </div>
              )}
              {getHtmlByKey(
                marketingCondition
                  ? "dragzone-tooltip-text-marketing"
                  : `dragzone-tooltip-text${mobileCondition ? "-mobile" : ""}`
              )}
            </div>
            <div className="tooltip-arrow"></div>
          </div>
        )}
        <div
          className={`outer-circle${dragged ? " dragged" : ""}`}
          ref={outerCircleRef}
        >
          <div
            className={`inner-circle${
              dragged ? (showEmail ? " dragged-contact" : " dragged") : ""
            }`}
          >
            <Icon
              name="upload-home"
              iconClass={"upload-cloud-icon" + (hover ? " hovered" : "")}
              white
              onClick={handleAddFiles}
              onMouseEnter={() => {
                setHover(true);
              }}
              onMouseLeave={() => {
                setHover(false);
              }}
            />
            <button
              className={`select-files-button${hover ? " hovered" : ""}`}
              onClick={handleAddFiles}
              onMouseEnter={() => {
                setHover(true);
              }}
              onMouseLeave={() => {
                setHover(false);
              }}
            >
              {getTextByKey("select-files")}
            </button>
            <button className="select-folder-button" onClick={handleAddFolders}>
              <Icon name="folders" white /> {getTextByKey("select-folder")}
            </button>
            <div className="drop-here-text">
              {getTextByKey(showEmail ? "send-file-to-text" : "drop-here-text")}
            </div>
            <AnimationContainer
              animationClassName={"height-in"}
              animationOn={showEmail}
            >
              <div className="drop-name-text">{contactName || ""}</div>
              <div className="drop-email-text">{showEmail ? drag : ""}</div>
            </AnimationContainer>
          </div>
          <AnimationContainer
            animationClassName={"fade-in"}
            animationOn={dragged}
          >
            <div className="highlighted-dropzone">
              <div className="highlighted-dropzone-circle" />
            </div>
          </AnimationContainer>
        </div>
      </AnimationContainer>
      <ContactsCircle drag={drag} />
      <div className="hidden-inputs">
        <input type="file" className="files-input" ref={filesRef} multiple />
        <input
          type="file"
          className="folders-input"
          aria-hidden="true"
          webkitdirectory=""
          directory=""
          multiple
          ref={folderRef}
        ></input>
      </div>
    </div>
  );
};
